import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AppShellProvider } from './contexts/AppShellContext';
import { ChatsProvider } from './contexts/ChatsContext';
import { ProxyDataProvider } from './contexts/ProxyDataContext';
import { RegulatoryDataProvider } from './contexts/RegulatoryDataContext';
import { EmailInboxProvider } from './contexts/EmailInboxContext';
import { TeamMessagesProvider } from './contexts/TeamMessagesContext';
import { EscalationContactProvider } from './contexts/EscalationContactContext';

import { LoginPage }     from './components/auth/LoginPage';
import { ProtectedRoute, PageGate, DefaultRedirect } from './components/auth/ProtectedRoute';
import { AppShell }      from './components/layout/AppShell';

import { AcePage }             from './pages/AcePage';
import { ReceivingLogPage }    from './pages/ReceivingLogPage';
import { InventoryPage }       from './pages/InventoryPage';
import { MaterialReceiptsPage } from './pages/MaterialReceiptsPage';
import { ExceptionsPage }      from './pages/ExceptionsPage';
import { EscalationsPage }     from './pages/EscalationsPage';
import { FollowUpsPage } from './pages/FollowUpsPage';
import { OversPage } from './pages/OversPage';
import { IntakeQueuePage }     from './pages/IntakeQueuePage';
import { AllJobsPage }         from './pages/AllJobsPage';
import { HistoryPage }         from './pages/HistoryPage';
import { EmailIntakePage }     from './pages/EmailIntakePage';
import { AdvisorSupportPage }  from './pages/AdvisorSupportPage';
import { WorkflowLibraryPage } from './pages/WorkflowLibraryPage';
import { WorkflowDetailPage }  from './pages/WorkflowDetailPage';
import { UserManagementPage }  from './pages/UserManagementPage';
import { FeedbackManagementPage } from './pages/FeedbackManagementPage';
import { ProfilePage }         from './pages/ProfilePage';
import { ExtractionAccuracyPage } from './pages/ExtractionAccuracyPage';
import { ProxyIntakeQueuePage } from './pages/ProxyIntakeQueuePage';
import { ProxyExceptionsPage } from './pages/ProxyExceptionsPage';
import { ProxyEscalationsPage } from './pages/ProxyEscalationsPage';
import { ProxyFollowUpsPage } from './pages/ProxyFollowUpsPage';
import { ProxyManualPullPage } from './pages/ProxyManualPullPage';
import { ProxyAllEmailsPage } from './pages/ProxyAllEmailsPage';
import { ProxyCompletedPage } from './pages/ProxyCompletedPage';
import { ProxyReportsPage } from './pages/ProxyReportsPage';
import { ProxyReportDetailPage } from './pages/ProxyReportDetailPage';
import { ProxyFilingsPage }  from './pages/ProxyFilingsPage';
import { ProxyOutreachPage } from './pages/ProxyOutreachPage';
import { ProxyBoardPage }    from './pages/ProxyBoardPage';
import { ProxyRDQPage }      from './pages/ProxyRDQPage';
import { RegulatoryProductionLogPage } from './pages/RegulatoryProductionLogPage';

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppShellProvider>
          <ChatsProvider>
          <EscalationContactProvider>
          <ProxyDataProvider>
          <RegulatoryDataProvider>
          <TeamMessagesProvider>
          <EmailInboxProvider>
            <Routes>
              {/* Public */}
              <Route path="/login" element={<LoginPage />} />

              {/* Protected — all wrapped in AppShell */}
              <Route
                element={
                  <ProtectedRoute>
                    <AppShell />
                  </ProtectedRoute>
                }
              >
                {/* Each route is gated by its rbac page id (utils/rbac.ts) —
                    UX-level only; the backend enforces the data behind it. */}
                <Route path="/ace"                          element={<PageGate page="ace"><AcePage /></PageGate>} />
                <Route path="/history"                      element={<PageGate page="history"><HistoryPage /></PageGate>} />
                <Route path="/receiving-log"               element={<PageGate page="receiving-log"><ReceivingLogPage /></PageGate>} />
                <Route path="/production-log"               element={<PageGate page="production-log"><RegulatoryProductionLogPage /></PageGate>} />
                <Route path="/inventory"                   element={<PageGate page="inventory"><InventoryPage /></PageGate>} />
                <Route path="/all-receipts"                 element={<PageGate page="all-receipts"><MaterialReceiptsPage /></PageGate>} />
                <Route path="/jobs"                        element={<PageGate page="jobs"><AllJobsPage /></PageGate>} />
                {/* Exception inboxes — one combined Exceptions page (per-type via
                    ?type=) plus the role-owned Escalations / Follow Ups queues.
                    Old per-type URLs redirect so bookmarks keep working. */}
                <Route path="/exceptions"                  element={<PageGate page="exceptions"><ExceptionsPage /></PageGate>} />
                <Route path="/escalations"                 element={<PageGate page="escalations"><EscalationsPage /></PageGate>} />
                <Route path="/follow-ups"                  element={<PageGate page="follow-ups"><FollowUpsPage /></PageGate>} />
                <Route path="/overs"                       element={<PageGate page="overs"><OversPage /></PageGate>} />
                <Route path="/awaiting-reorder"            element={<Navigate to="/follow-ups" replace />} />
                <Route path="/no-job-found"                element={<Navigate to="/exceptions?type=no-job-found" replace />} />
                <Route path="/received-short"              element={<Navigate to="/exceptions?type=received-short" replace />} />
                <Route path="/on-hold"                     element={<Navigate to="/exceptions?type=on-hold" replace />} />
                <Route path="/intake-queue"                element={<PageGate page="intake-queue"><IntakeQueuePage /></PageGate>} />
                <Route path="/email-intake"               element={<PageGate page="email-intake"><EmailIntakePage /></PageGate>} />
                <Route path="/advisor-support"            element={<PageGate page="advisor-support"><AdvisorSupportPage /></PageGate>} />
                <Route path="/workflows"                   element={<PageGate page="workflows"><WorkflowLibraryPage /></PageGate>} />
                <Route path="/workflows/:workflowId"       element={<PageGate page="workflows"><WorkflowDetailPage /></PageGate>} />
                <Route path="/admin/users"                 element={<PageGate page="admin-users"><UserManagementPage /></PageGate>} />
                <Route path="/admin/feedback"               element={<PageGate page="admin-feedback"><FeedbackManagementPage /></PageGate>} />
                <Route path="/profile"                     element={<ProfilePage />} />
                <Route path="/analytics/accuracy-dashboard" element={<PageGate page="extraction-accuracy"><ExtractionAccuracyPage /></PageGate>} />
                <Route path="/data/extraction-accuracy"    element={<Navigate to="/analytics/accuracy-dashboard" replace />} />
                <Route path="/analytics/scan-analytics"    element={<Navigate to="/analytics/accuracy-dashboard" replace />} />

                {/* PROXY (ICT/Collections) — deliberately NOT PageGate-wrapped;
                    zero RBAC gating by design (mirrors /profile above).
                    "Escalate to manager" is informational only, never blocking. */}
                <Route path="/proxy"                  element={<ProxyIntakeQueuePage />} />
                <Route path="/proxy/exceptions"        element={<ProxyExceptionsPage />} />
                <Route path="/proxy/escalations"       element={<ProxyEscalationsPage />} />
                <Route path="/proxy/follow-ups"        element={<ProxyFollowUpsPage />} />
                <Route path="/proxy/done"              element={<Navigate to="/proxy/completed" replace />} />
                <Route path="/email-inbox"              element={<ProxyAllEmailsPage />} />
                <Route path="/email-inbox/fulfillment" element={<ProxyAllEmailsPage />} />
                <Route path="/proxy/emails"            element={<Navigate to="/email-inbox" replace />} />
                <Route path="/proxy/deadlines"         element={<Navigate to="/proxy/reports/outstanding-all" replace />} />
                <Route path="/proxy/completed"         element={<ProxyCompletedPage />} />
                <Route path="/proxy/activity-log"      element={<Navigate to="/proxy/completed" replace />} />
                <Route path="/proxy/reports"           element={<ProxyReportsPage />} />
                <Route path="/proxy/reports/manual-pull" element={<ProxyManualPullPage />} />
                <Route path="/proxy/reports/:reportId" element={<ProxyReportDetailPage />} />
                <Route path="/proxy/filings"   element={<ProxyFilingsPage />} />
                <Route path="/proxy/outreach"  element={<ProxyOutreachPage />} />
                <Route path="/proxy/board"     element={<ProxyBoardPage />} />
                <Route path="/proxy/rdq"       element={<ProxyRDQPage />} />
              </Route>

              {/* Legacy redirects */}
              <Route path="/home"             element={<DefaultRedirect />} />
              <Route path="/fulfill"          element={<Navigate to="/receiving-log"    replace />} />
              <Route path="/fulfill/*"        element={<Navigate to="/all-receipts"      replace />} />
              <Route path="/job-matching"     element={<DefaultRedirect />} />
              <Route path="/dashboard"        element={<DefaultRedirect />} />

              {/* Default — lands each binding on its first visible page */}
              <Route path="/"  element={<DefaultRedirect />} />
              <Route path="*"  element={<DefaultRedirect />} />
            </Routes>
          </EmailInboxProvider>
          </TeamMessagesProvider>
          </RegulatoryDataProvider>
          </ProxyDataProvider>
          </EscalationContactProvider>
          </ChatsProvider>
        </AppShellProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;